import api from './api';
import { ApiResponse, unwrapApiResponse } from '../utils/api-response.util';
import { Locale } from './workout.service';
import {
  FoodResponseDto,
  GetFoodsParams,
  PaginatedFoodsResponseDto,
  FoodSuggestion,
  CreateFoodDto,
  UpdateFoodDto,
  MergeFoodDto,
  FoodSourceType,
  FoodCategory,
  MeasurementUnit,
  FoodOptionsResponseDto
} from './food.service';
import { ImportResult, ImportSession } from '../types/import.types';

export type ImportFoodsResponseDto = ImportResult;
export type ImportSessionDto = ImportSession;

export interface FoodSiteContentDto {
  foodId: number;
  prepareTimeMinutes: number | null;
  difficulty: 'easy' | 'medium' | 'hard' | null;
  portionWeightG: number | null;
  ingredientsText: string | null;
  recipeText: string | null;
}

export interface UpsertFoodSiteContentDto {
  prepareTimeMinutes?: number | null;
  difficulty?: 'easy' | 'medium' | 'hard' | null;
  portionWeightG?: number | null;
  ingredientsText?: string | null;
  recipeText?: string | null;
}



// Interface for the edit response which includes nameI18n
export interface FoodEditResponseDto extends FoodResponseDto {
  nameI18n: { [key in Locale]?: string };
}

// Interface for the detailed response
export interface FoodDetailDto {
  id: number;
  nameI18n: { [key in Locale]?: string };
  detailsI18n?: { [key in Locale]?: string }; // Added detailsI18n
  category: FoodCategory;
  measurementUnit: MeasurementUnit | null;
  gramsPerUnit: number | null;
  defaultMeasurementValue: number | null;
  
  caloriesPer100g: number | null;
  proteinPer100g: number | null;
  fatPer100g: number | null;
  carbsPer100g: number | null;
  fiberPer100g: number | null;

  isStructured: boolean;
  isSystem: boolean; // Added isSystem
  imageId: number | null;
  imageUrl: string | null;
  externalLink: string | null;

  createdByUserId: number | null;
  creator: {
    id: number;
    fullName: string;
    email: string;
  } | null;

  usageCount: number;
  createdAt: string;
  updatedAt: string;
  
  legacyId?: string | null; // Added legacyId
  legacyEntity?: 'product' | 'dish' | null; // Added legacyEntity
  legacyImg?: string | null; // Added legacyImg
}

class AdminFoodService {
  private readonly BASE_URL = '/admin/lib/foods';
  private readonly IMPORT_BASE_URL = '/admin/lib/import';

  async getFoods(params: GetFoodsParams): Promise<PaginatedFoodsResponseDto> {
    const response = await api.get<any>(`${this.BASE_URL}`, {
      params,
    });
    
    // Manually handle the response structure if unwrapApiResponse doesn't handle meta correctly for this specific endpoint
    // Or ensure unwrapApiResponse is used correctly.
    // Based on previous code, it seems unwrapApiResponse returns just 'data'.
    // But we need 'meta' as well.
    
    const body = response.data;
    if (body && body.success && Array.isArray(body.data)) {
      return {
        data: body.data,
        meta: body.meta,
      };
    }
    
    // Fallback or error handling
    console.error('Unexpected API response structure for admin getFoods:', body);
    return { 
      data: [], 
      meta: { total: 0, page: 1, limit: params.limit || 10 } 
    };
  }

  async getFoodSuggestions(query: string): Promise<FoodSuggestion[]> {
    const response = await api.get<ApiResponse<FoodSuggestion[]>>(`${this.BASE_URL}/search`, { params: { query } });
    return unwrapApiResponse<FoodSuggestion[]>(response);
  }

  async searchSystemFoods(query: string, limit: number = 10): Promise<FoodResponseDto[]> {
    const params: GetFoodsParams = {
      search: query,
      limit: limit,
      sourceType: FoodSourceType.SYSTEM,
    };
    const response = await this.getFoods(params);
    return response.data;
  }

  async getFoodForEdit(id: number): Promise<FoodEditResponseDto> {
    // Use the /edit endpoint to get full data including translations
    const response = await api.get<ApiResponse<FoodEditResponseDto>>(`${this.BASE_URL}/${id}/edit`);
    return unwrapApiResponse<FoodEditResponseDto>(response);
  }

  async getFoodDetail(id: number): Promise<FoodDetailDto> {
    const response = await api.get<ApiResponse<FoodDetailDto>>(`${this.BASE_URL}/${id}/detail`);
    return unwrapApiResponse<FoodDetailDto>(response);
  }

  async createFood(payload: CreateFoodDto): Promise<FoodResponseDto> {
    const response = await api.post<ApiResponse<FoodResponseDto>>(`${this.BASE_URL}`, payload);
    return unwrapApiResponse<FoodResponseDto>(response);
  }

  async updateFood(id: number, payload: UpdateFoodDto): Promise<FoodResponseDto> {
    const response = await api.patch<ApiResponse<FoodResponseDto>>(`${this.BASE_URL}/${id}`, payload);
    return unwrapApiResponse<FoodResponseDto>(response);
  }

  async deleteFood(id: number): Promise<void> {
    await api.delete<ApiResponse<void>>(`${this.BASE_URL}/${id}`);
  }

  async mergeFoods(payload: MergeFoodDto): Promise<{ removedFood: FoodResponseDto; targetFood: FoodResponseDto; updatedCalendarEvents: number }> {
    const response = await api.post<ApiResponse<{ removedFood: FoodResponseDto; targetFood: FoodResponseDto; updatedCalendarEvents: number }>>(`${this.BASE_URL}/merge`, payload);
    return unwrapApiResponse<{ removedFood: FoodResponseDto; targetFood: FoodResponseDto; updatedCalendarEvents: number }>(response);
  }

  // Options
  async getFoodOptions(): Promise<FoodOptionsResponseDto> {
    const response = await api.get<ApiResponse<FoodOptionsResponseDto>>(`${this.BASE_URL}/options`);
    return unwrapApiResponse<FoodOptionsResponseDto>(response);
  }

  // Import functionality
  async downloadImportTemplate(): Promise<Blob> {
    const response = await api.get(`${this.IMPORT_BASE_URL}/foods/template`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async importFoods(file: File): Promise<ImportResult> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post<ApiResponse<ImportResult>>(`${this.IMPORT_BASE_URL}/foods`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return unwrapApiResponse<ImportResult>(response);
  }

  async getImportHistory(): Promise<ImportSession[]> {
    const response = await api.get<ApiResponse<ImportSession[]>>(`${this.IMPORT_BASE_URL}/history`);
    return unwrapApiResponse<ImportSession[]>(response);
  }

  async revertImport(sessionId: number): Promise<void> {
    await api.delete<ApiResponse<void>>(`${this.IMPORT_BASE_URL}/${sessionId}`);
  }

  async toggleSitePublished(id: number): Promise<{ id: number; isSitePublished: boolean }> {
    const response = await api.patch<ApiResponse<{ id: number; isSitePublished: boolean }>>(`${this.BASE_URL}/${id}/publish`);
    return unwrapApiResponse(response);
  }

  async getSiteContent(id: number): Promise<FoodSiteContentDto | null> {
    const response = await api.get<ApiResponse<FoodSiteContentDto | null>>(`${this.BASE_URL}/${id}/site-content`);
    return unwrapApiResponse(response);
  }

  async upsertSiteContent(id: number, payload: UpsertFoodSiteContentDto): Promise<FoodSiteContentDto> {
    const response = await api.patch<ApiResponse<FoodSiteContentDto>>(`${this.BASE_URL}/${id}/site-content`, payload);
    return unwrapApiResponse(response);
  }
}

export const adminFoodService = new AdminFoodService();
export type { FoodOptionsResponseDto };
